import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useHistory } from 'react-router'
import MakeHeader from '../ReusableComponents/Header/MakeHeader'

const BACKEND_URL = process.env.REACT_APP_API_BASE

const DEVICES = [
    { value: 'peeCee',    label: 'PeeCee  (PeeCee1 / PeeCee2)' },
    { value: 'toki',      label: 'Toki  (Toki1)' },
    { value: 'poki',      label: 'Poki  (Poki1)' },
    { value: 'moki',      label: 'Moki  (Moki1)' },
    { value: 'zing',      label: 'Zing  (Zing0–3)' },
    { value: 'crawle',    label: 'Crawle  (Crawle1–3)' },
    { value: 'roboki',    label: 'Roboki  (Roboki1)' },
    { value: 'klawb',     label: 'Klawb  (Klawb1)' },
    { value: 'atr',       label: 'Atr' },
    { value: 'tokiPluse', label: 'TokiPlus' },
]
const LANGUAGES = [
    { value: 'c',      label: 'C / Arduino (.ino)' },
    { value: 'python', label: 'MicroPython (.py)' },
]

const deviceLabel  = v => DEVICES.find(d => d.value === v)?.label?.split('  ')[0] || v
const languageLabel = v => LANGUAGES.find(l => l.value === v)?.label || v

// ─── data helpers ─────────────────────────────────────────────────────────────
const mk = {
    section:  () => ({ header: '', description: '' }),
    entry:    () => ({ name: '', description: '', sections: [], examples: [] }),
    category: () => ({ title: '', entries: [{ name: '', description: '', sections: [], examples: [] }] }),
    doc:      () => ({ referenceList: ['functions'], referenceListDetails: { functions: { title: 'Functions', categories: [] } } }),
}
const cp = o => JSON.parse(JSON.stringify(o))

// ─── shared styles ────────────────────────────────────────────────────────────
const inp  = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, color: '#1e293b', background: '#fff', boxSizing: 'border-box', width: '100%' }
const addB = { background: '#f5f3ff', color: '#7c3aed', border: '1px dashed #c4b5fd', borderRadius: 6, padding: '5px 11px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }
const lbl  = { display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.4px' }
const delB = { background: 'none', border: '1px solid #fca5a5', color: '#dc2626', cursor: 'pointer', fontWeight: 600, fontSize: 11, padding: '2px 8px', borderRadius: 4, flexShrink: 0 }

// ─── Toast ────────────────────────────────────────────────────────────────────
function useToast() {
    const [t, setT] = useState(null)
    const show = (msg, isError = false) => { setT({ msg, isError }); setTimeout(() => setT(null), 4000) }
    return [t, show]
}
function Toast({ t }) {
    if (!t) return null
    return <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '12px 20px', borderRadius: 8, fontWeight: 600, fontSize: 14, boxShadow: '0 4px 14px rgba(0,0,0,.14)', background: t.isError ? '#fee2e2' : '#dcfce7', color: t.isError ? '#b91c1c' : '#15803d', border: `1px solid ${t.isError ? '#fca5a5' : '#86efac'}` }}>{t.msg}</div>
}

// ─── Preview modal — pixel-perfect copy of ReferencesOverlay.tsx ─────────────
function PreviewModal({ doc, device, language, onClose }) {
    const cats = doc?.referenceListDetails?.functions?.categories || []
    const [selFn, setSelFn] = useState(null)   // { fn, ei, ci }
    const [search, setSearch] = useState('')
    const [copied, setCopied] = useState(false)

    const langBadge = language === 'python' ? 'MicroPython' : 'C / Arduino'

    const handleSearch = val => {
        setSearch(val)
        if (!val.trim()) { setSelFn(null); return }
        for (let ci = 0; ci < cats.length; ci++) {
            const ei = (cats[ci].entries || []).findIndex(e => e.name?.toLowerCase().includes(val.toLowerCase()))
            if (ei !== -1) { setSelFn({ fn: cats[ci].entries[ei], ei, ci }); return }
        }
        setSelFn(null)
    }

    const handleCopy = async code => {
        setCopied(true)
        try { await navigator.clipboard.writeText(code) } catch (_) {}
        setTimeout(() => setCopied(false), 600)
    }

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
            {/* matches: absolute z-50 bg-white rounded-3xl max-w-7xl w-[64vw] overflow-hidden border border-purple-100 shadow-xl */}
            <div style={{ background: '#fff', borderRadius: 24, width: '64vw', maxWidth: 1152, overflow: 'hidden', border: '1px solid #f3e8ff', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }} onClick={e => e.stopPropagation()}>
                <div style={{ position: 'relative' }}>

                    {/* close — absolute top-0 right-0 p-2 */}
                    <button onClick={onClose} style={{ position: 'absolute', top: 0, right: 0, padding: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', zIndex: 10, lineHeight: 1 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>

                    {/* Top Header — flex items-center pl-6 pr-10 justify-between h-20 border-b border-gray-200 */}
                    <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 24, paddingRight: 40, justifyContent: 'space-between', height: 80, borderBottom: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#111827' }}>References</h2>
                            {/* text-xs font-semibold px-2.5 py-0.5 rounded-full bg-purple-50 text-[#653AE6] border border-purple-200 */}
                            <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 99, background: '#faf5ff', color: '#653AE6', border: '1px solid #e9d5ff' }}>{langBadge}</span>
                        </div>
                        {/* w-72 mr-6 — search bar */}
                        <div style={{ width: 288, marginRight: 24, position: 'relative' }}>
                            <input value={search} onChange={e => handleSearch(e.target.value)} placeholder="Search functions..."
                                style={{ width: '100%', padding: '8px 12px 8px 36px', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 13, color: '#374151', outline: 'none', background: '#f9fafb', boxSizing: 'border-box' }} />
                            <svg style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        </div>
                    </div>

                    {/* body — flex h-[69vh] */}
                    <div style={{ display: 'flex', height: '69vh' }}>

                        {/* Sidebar — w-64 border-r border-gray-200 p-4 flex flex-col justify-between shrink-0 */}
                        <div style={{ width: 256, borderRight: '1px solid #e5e7eb', padding: 16, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', flexShrink: 0 }}>
                            <div style={{ overflowY: 'auto', flex: 1, paddingRight: 4 }}>
                                {/* text-xs text-[#535862] mb-1 */}
                                <p style={{ fontSize: 12, color: '#535862', marginBottom: 4, marginTop: 0 }}>
                                    For controlling the Arduino board and performing computations
                                </p>
                                {cats.map((cat, ci) => (
                                    <div key={ci} style={{ marginBottom: 16 }}>
                                        {/* text-xs font-semibold text-[#717680] uppercase tracking-wider px-1 pt-1 mb-1 */}
                                        <h3 style={{ fontSize: 12, fontWeight: 600, color: '#717680', textTransform: 'uppercase', letterSpacing: '.05em', padding: '4px 4px 4px', margin: '0 0 4px' }}>{cat.title}</h3>
                                        {(cat.entries || []).map((en, ei) => {
                                            const active = selFn?.ci === ci && selFn?.ei === ei
                                            return (
                                                <button key={ei} onClick={() => setSelFn({ fn: en, ei, ci })}
                                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', textAlign: 'left', padding: '6px 12px', borderRadius: 12, marginBottom: 4, fontSize: 14, fontWeight: active ? 600 : 500, cursor: 'pointer', border: active ? '1px solid #DDD0FE' : '1px solid transparent', background: active ? '#F9F5FF' : 'transparent', color: active ? '#653AE6' : '#475467' }}>
                                                    {en.name}
                                                </button>
                                            )
                                        })}
                                    </div>
                                ))}
                                {cats.length === 0 && <p style={{ fontSize: 13, color: '#d1d5db', textAlign: 'center', marginTop: 24 }}>No categories</p>}
                            </div>

                            {/* Import button — mt-6 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-400 text-white rounded-lg */}
                            <button style={{ marginTop: 24, padding: '8px 16px', background: 'linear-gradient(to right, #a855f7, #c084fc)', color: '#fff', borderRadius: 8, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 14, cursor: 'pointer', boxShadow: '0 2px 6px rgba(168,85,247,.35)' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                                Import
                            </button>
                        </div>

                        {/* Main Content — flex-1 p-6 overflow-y-auto bg-[#FAF9FF] */}
                        <div style={{ flex: 1, padding: 24, overflowY: 'auto', background: '#FAF9FF' }}>
                            {selFn?.fn ? (
                                <div style={{ maxWidth: 672 }}>
                                    {/* space-y-5 */}
                                    <div style={{ marginBottom: 20 }}>
                                        {/* text-xl font-bold text-[#181D27] mb-1.5 */}
                                        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#181D27', marginBottom: 6, marginTop: 0 }}>{selFn.fn.name}</h2>
                                        {/* text-sm text-[#535862] leading-relaxed */}
                                        <p style={{ fontSize: 14, color: '#535862', lineHeight: 1.6, margin: 0 }}>{selFn.fn.description}</p>
                                    </div>

                                    {/* sections — bg-white rounded-xl border border-gray-100 p-4 shadow-xs */}
                                    {(selFn.fn.sections || []).map((sec, si) => (
                                        <div key={si} style={{ background: '#fff', borderRadius: 12, border: '1px solid #f3f4f6', padding: 16, marginBottom: 12, boxShadow: '0 1px 2px rgba(0,0,0,.04)' }}>
                                            {/* text-xs font-semibold uppercase tracking-wider text-[#717680] mb-1 */}
                                            <h3 style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: '#717680', marginBottom: 4, marginTop: 0 }}>{sec.header}</h3>
                                            {/* text-sm text-[#344054] whitespace-pre-line leading-relaxed */}
                                            <p style={{ margin: 0, fontSize: 14, color: '#344054', whiteSpace: 'pre-line', lineHeight: 1.6 }}>{sec.description}</p>
                                        </div>
                                    ))}

                                    {/* examples */}
                                    {(selFn.fn.examples || []).length > 0 && (
                                        <div style={{ paddingTop: 8 }}>
                                            {/* text-xs font-semibold uppercase tracking-wider text-[#717680] */}
                                            <h3 style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: '#717680', marginBottom: 12 }}>Examples</h3>
                                            {selFn.fn.examples.map((ex, i) => (
                                                <div key={i} style={{ borderRadius: 12, border: '1px solid #e5e7eb', background: '#fff', overflow: 'hidden', marginBottom: 12, boxShadow: '0 1px 2px rgba(0,0,0,.04)' }}>
                                                    {/* drag handle row — px-3.5 py-2 bg-[#F8F9FA] border-b border-gray-100 */}
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', background: '#F8F9FA', borderBottom: '1px solid #f3f4f6' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, color: '#535862' }}>
                                                            {/* Grip icon */}
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><circle cx="9" cy="5" r="1" fill="#9ca3af"/><circle cx="9" cy="12" r="1" fill="#9ca3af"/><circle cx="9" cy="19" r="1" fill="#9ca3af"/><circle cx="15" cy="5" r="1" fill="#9ca3af"/><circle cx="15" cy="12" r="1" fill="#9ca3af"/><circle cx="15" cy="19" r="1" fill="#9ca3af"/></svg>
                                                            <span>Example {i + 1}</span>
                                                        </div>
                                                        {/* copy button */}
                                                        <button onClick={() => handleCopy(ex)} style={{ padding: '4px 8px', borderRadius: 8, color: copied ? '#059669' : '#6b7280', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500 }}>
                                                            {copied
                                                                ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                                                                : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>}
                                                        </button>
                                                    </div>
                                                    {/* code — p-1 with Monaco-style monospace block */}
                                                    <div style={{ padding: 4, background: '#fff' }}>
                                                        <pre style={{ margin: 0, padding: '8px 12px', fontSize: 13, color: '#1e293b', fontFamily: '"Fira Code","Cascadia Code","Courier New",monospace', whiteSpace: 'pre-wrap', lineHeight: 1.6, background: '#fff' }}>{ex}</pre>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                /* empty state — h-full flex flex-col items-center justify-center */
                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 32 }}>
                                    {/* w-12 h-12 rounded-2xl bg-purple-50 border border-purple-100 */}
                                    <div style={{ width: 48, height: 48, borderRadius: 16, background: '#faf5ff', border: '1px solid #f3e8ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#653AE6', marginBottom: 12 }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: 'rotate(90deg)' }}><circle cx="9" cy="5" r="1" fill="currentColor"/><circle cx="9" cy="12" r="1" fill="currentColor"/><circle cx="9" cy="19" r="1" fill="currentColor"/><circle cx="15" cy="5" r="1" fill="currentColor"/><circle cx="15" cy="12" r="1" fill="currentColor"/><circle cx="15" cy="19" r="1" fill="currentColor"/></svg>
                                    </div>
                                    <h3 style={{ fontSize: 15, fontWeight: 700, color: '#181D27', margin: '0 0 4px' }}>Select a reference</h3>
                                    <p style={{ fontSize: 14, color: '#535862', maxWidth: 280, margin: 0, lineHeight: 1.6 }}>Choose any function from the list on the left to view its details and examples.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

// ─── Section / Example rows ───────────────────────────────────────────────────
function SectionRow({ sec, onChange, onDel }) {
    return (
        <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'flex-start' }}>
            <input value={sec.header} onChange={e => onChange({ ...sec, header: e.target.value })} placeholder="Header (e.g. Syntax)" style={{ ...inp, width: 140, flexShrink: 0 }} />
            <textarea value={sec.description} onChange={e => onChange({ ...sec, description: e.target.value })} placeholder="Content…" rows={2} style={{ ...inp, flex: 1, resize: 'vertical', fontFamily: 'inherit' }} />
            <button onClick={onDel} style={delB}>✕</button>
        </div>
    )
}
function ExampleRow({ ex, index, onChange, onDel, lang }) {
    return (
        <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>Example {index + 1}</span>
                <button onClick={onDel} style={delB}>✕ Remove</button>
            </div>
            <textarea value={ex} onChange={e => onChange(e.target.value)} rows={4}
                placeholder={lang === 'python' ? 'led.on()' : 'digitalWrite(13, HIGH);'}
                style={{ ...inp, fontFamily: '"Fira Code","Courier New",monospace', fontSize: 12, resize: 'vertical' }} />
        </div>
    )
}

// ─── Entry card ───────────────────────────────────────────────────────────────
function EntryCard({ entry, ci, ei, lang, doc, setDoc }) {
    const [open, setOpen] = useState(true)
    const patch = fn => { const d = cp(doc); fn(d.referenceListDetails.functions.categories[ci].entries[ei]); setDoc(d) }
    return (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 8, background: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: open ? '#f8fafc' : '#fff', borderRadius: open ? '8px 8px 0 0' : 8, borderBottom: open ? '1px solid #e2e8f0' : 'none' }}>
                <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#64748b', padding: 0 }}>{open ? '▾' : '▸'}</button>
                <input value={entry.name} onChange={e => patch(en => { en.name = e.target.value })} placeholder="Function name (e.g. pinMode)" style={{ ...inp, flex: 1, fontWeight: 600 }} />
                <button onClick={() => { const d = cp(doc); d.referenceListDetails.functions.categories[ci].entries.splice(ei, 1); setDoc(d) }} style={delB}>✕ Delete</button>
            </div>
            {open && (
                <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                        <label style={lbl}>Description</label>
                        <textarea value={entry.description} onChange={e => patch(en => { en.description = e.target.value })} placeholder="What does this do?" rows={2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
                    </div>
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <label style={{ ...lbl, margin: 0 }}>Sections <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none', fontSize: 11 }}>(Syntax, Parameters, Returns…)</span></label>
                            <button style={addB} onClick={() => patch(en => { en.sections.push(mk.section()) })}>+ Add Section</button>
                        </div>
                        {entry.sections.length === 0 && <p style={{ margin: 0, fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>No sections yet.</p>}
                        {entry.sections.map((sec, si) => (
                            <SectionRow key={si} sec={sec}
                                onChange={upd => { const d = cp(doc); d.referenceListDetails.functions.categories[ci].entries[ei].sections[si] = upd; setDoc(d) }}
                                onDel={() => { const d = cp(doc); d.referenceListDetails.functions.categories[ci].entries[ei].sections.splice(si, 1); setDoc(d) }} />
                        ))}
                    </div>
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <label style={{ ...lbl, margin: 0 }}>Code Examples</label>
                            <button style={addB} onClick={() => patch(en => { en.examples.push('') })}>+ Add Example</button>
                        </div>
                        {entry.examples.length === 0 && <p style={{ margin: 0, fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>No examples yet.</p>}
                        {entry.examples.map((ex, exI) => (
                            <ExampleRow key={exI} ex={ex} index={exI} lang={lang}
                                onChange={val => { const d = cp(doc); d.referenceListDetails.functions.categories[ci].entries[ei].examples[exI] = val; setDoc(d) }}
                                onDel={() => { const d = cp(doc); d.referenceListDetails.functions.categories[ci].entries[ei].examples.splice(exI, 1); setDoc(d) }} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

// ─── Category card ────────────────────────────────────────────────────────────
function CategoryCard({ cat, ci, lang, doc, setDoc }) {
    const [open, setOpen] = useState(true)
    return (
        <div style={{ border: '1.5px solid #c4b5fd', borderRadius: 10, marginBottom: 14, background: '#faf5ff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: open ? '#ede9fe' : '#f5f3ff', borderRadius: open ? '10px 10px 0 0' : 10, borderBottom: open ? '1.5px solid #c4b5fd' : 'none' }}>
                <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#7c3aed', padding: 0 }}>{open ? '▾' : '▸'}</button>
                <input value={cat.title} onChange={e => { const d = cp(doc); d.referenceListDetails.functions.categories[ci].title = e.target.value; setDoc(d) }} placeholder="Category title (e.g. Digital I/O)" style={{ ...inp, flex: 1, fontWeight: 700, color: '#4c1d95', fontSize: 14 }} />
                <button onClick={() => { const d = cp(doc); d.referenceListDetails.functions.categories.splice(ci, 1); setDoc(d) }} style={delB}>✕ Delete Category</button>
            </div>
            {open && (
                <div style={{ padding: '12px 14px' }}>
                    {cat.entries.map((en, ei) => <EntryCard key={ei} entry={en} ci={ci} ei={ei} lang={lang} doc={doc} setDoc={setDoc} />)}
                    <button style={{ ...addB, display: 'block', width: '100%', textAlign: 'center', padding: '9px 0', marginTop: 4 }} onClick={() => { const d = cp(doc); d.referenceListDetails.functions.categories[ci].entries.push(mk.entry()); setDoc(d) }}>
                        + Add Function / Entry
                    </button>
                </div>
            )}
        </div>
    )
}

// ─── LIST VIEW ────────────────────────────────────────────────────────────────
function ListView({ onEdit, showToast }) {
    const [rows, setRows]         = useState([])
    const [loading, setLoading]   = useState(true)
    const [filterDev, setFilterDev]   = useState('all')
    const [filterLang, setFilterLang] = useState('all')
    const [preview, setPreview]   = useState(null)   // { doc, device, language }

    const loadAll = useCallback(async () => {
        setLoading(true)
        try {
            const res  = await fetch(`${BACKEND_URL}/api/admin/scripting-example-code`, { credentials: 'include' })
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || res.status)
            setRows(data)
        } catch (e) { showToast(`Load failed: ${e.message}`, true) }
        setLoading(false)
    }, [showToast])

    useEffect(() => { loadAll() }, [loadAll])

    const handleDelete = async (device, language) => {
        if (!window.confirm(`Delete references for ${deviceLabel(device)} / ${languageLabel(language)}?`)) return
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/scripting-example-code/${device}/${language}/references`, { method: 'DELETE', credentials: 'include' })
            if (!res.ok) throw new Error(res.status)
            showToast('Deleted. Scripting now uses built-in defaults for this combo.')
            loadAll()
        } catch (e) { showToast(`Delete failed: ${e.message}`, true) }
    }

    const filtered = rows.filter(r =>
        (filterDev  === 'all' || r.device   === filterDev) &&
        (filterLang === 'all' || r.language  === filterLang)
    )

    const totalFns = doc => (doc?.referenceListDetails?.functions?.categories || []).reduce((s, c) => s + (c.entries?.length || 0), 0)
    const totalCats = doc => (doc?.referenceListDetails?.functions?.categories || []).length

    return (
        <div style={{ padding: '20px', background: '#f8f9fa', minHeight: '100vh' }}>
            {preview && <PreviewModal doc={preview.doc} device={preview.device} language={preview.language} onClose={() => setPreview(null)} />}

            <MakeHeader title="Admin: Scripting Reference Code" isGoBack={true} goBack={() => window.history.back()} />

            <div style={{ maxWidth: 960, margin: '24px auto', background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 4px 12px rgba(0,0,0,.05)' }}>

                {/* top bar */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 20 }}>
                    <div>
                        <label style={lbl}>Filter by Device</label>
                        <select value={filterDev} onChange={e => setFilterDev(e.target.value)} style={{ ...inp, minWidth: 220, cursor: 'pointer' }}>
                            <option value="all">All Devices</option>
                            {DEVICES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={lbl}>Filter by Language</label>
                        <select value={filterLang} onChange={e => setFilterLang(e.target.value)} style={{ ...inp, minWidth: 180, cursor: 'pointer' }}>
                            <option value="all">All Languages</option>
                            {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                        </select>
                    </div>
                    <button onClick={() => onEdit(null)} style={{ marginLeft: 'auto', padding: '9px 22px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', boxShadow: '0 2px 8px rgba(124,58,237,.3)' }}>
                        + Add New
                    </button>
                </div>

                {/* table */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '50px 0', color: '#94a3b8' }}>Loading…</div>
                ) : filtered.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '50px 0', color: '#94a3b8', border: '2px dashed #e2e8f0', borderRadius: 10 }}>
                        No custom references saved yet. Click <strong>+ Add New</strong> to create one.
                    </div>
                ) : (
                    <div style={{ maxHeight: '62vh', overflowY: 'auto', paddingRight: 4 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                    {['Device', 'Language', 'Categories', 'Functions', 'Last Updated', 'Actions'].map(h => (
                                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((row, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }} onMouseEnter={e => e.currentTarget.style.background = '#faf5ff'} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                                        <td style={{ padding: '12px 14px', fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{deviceLabel(row.device)}</td>
                                        <td style={{ padding: '12px 14px' }}>
                                            <span style={{ background: row.language === 'python' ? '#eff6ff' : '#f0fdf4', color: row.language === 'python' ? '#1d4ed8' : '#15803d', border: `1px solid ${row.language === 'python' ? '#bfdbfe' : '#bbf7d0'}`, borderRadius: 99, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                                                {languageLabel(row.language)}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px 14px', fontSize: 14, color: '#475467' }}>{totalCats(row.content)}</td>
                                        <td style={{ padding: '12px 14px', fontSize: 14, color: '#475467' }}>{totalFns(row.content)}</td>
                                        <td style={{ padding: '12px 14px', fontSize: 12, color: '#94a3b8' }}>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}</td>
                                        <td style={{ padding: '12px 14px' }}>
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                <button onClick={() => setPreview({ doc: row.content, device: row.device, language: row.language })} style={actionBtn('#7c3aed')}>👁 Preview</button>
                                                <button onClick={() => onEdit({ device: row.device, language: row.language, doc: row.content })} style={actionBtn('#0ea5e9')}>✏️ Edit</button>
                                                <button onClick={() => handleDelete(row.device, row.language)} style={actionBtn('#dc2626')}>🗑 Delete</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )
}

// ─── EDIT VIEW ────────────────────────────────────────────────────────────────
function EditView({ initial, onBack, showToast }) {
    const importRef = useRef()
    const isNew     = !initial

    const [device, setDevice]     = useState(initial?.device   || 'peeCee')
    const [language, setLanguage] = useState(initial?.language || 'c')
    const [doc, setDoc]           = useState(initial?.doc      || mk.doc())
    const [saving, setSaving]     = useState(false)
    const [preview, setPreview]   = useState(false)

    const save = async () => {
        setSaving(true)
        try {
            const res  = await fetch(`${BACKEND_URL}/api/admin/scripting-example-code/${device}/${language}/references`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ content: doc }), credentials: 'include' })
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
            showToast('Saved & published! Scripting will use this on next load.')
            onBack()
        } catch (e) { showToast(`Save failed: ${e.message}`, true) }
        setSaving(false)
    }

    const exportJSON = () => {
        const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' })), download: `refs-${device}-${language}.json` })
        a.click()
    }
    const onImport = e => {
        const file = e.target.files?.[0]; if (!file) return
        const r = new FileReader()
        r.onload = ev => {
            try {
                const p = JSON.parse(ev.target.result)
                if (!p.referenceList || !p.referenceListDetails) throw new Error('Invalid ReferenceDocumentation JSON')
                setDoc(p); showToast('Imported — review then Save & Publish.')
            } catch (err) { showToast(`Import failed: ${err.message}`, true) }
        }
        r.readAsText(file); e.target.value = ''
    }

    const cats = doc?.referenceListDetails?.functions?.categories || []

    return (
        <div style={{ padding: '20px', background: '#f8f9fa', minHeight: '100vh' }}>
            {preview && <PreviewModal doc={doc} device={device} language={language} onClose={() => setPreview(false)} />}
            <input ref={importRef} type="file" accept="application/json" style={{ display: 'none' }} onChange={onImport} />

            <MakeHeader title={isNew ? 'Add Reference Code' : `Edit — ${deviceLabel(device)} / ${languageLabel(language)}`} isGoBack={true} goBack={onBack} />

            <div style={{ maxWidth: 960, margin: '24px auto', background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 4px 12px rgba(0,0,0,.05)' }}>

                {/* selectors — only editable when new */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginBottom: 16 }}>
                    <div>
                        <label style={lbl}>Device</label>
                        <select value={device} onChange={e => setDevice(e.target.value)} disabled={!isNew} style={{ ...inp, minWidth: 230, cursor: isNew ? 'pointer' : 'not-allowed', opacity: isNew ? 1 : .6 }}>
                            {DEVICES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={lbl}>Language</label>
                        <select value={language} onChange={e => setLanguage(e.target.value)} disabled={!isNew} style={{ ...inp, minWidth: 180, cursor: isNew ? 'pointer' : 'not-allowed', opacity: isNew ? 1 : .6 }}>
                            {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                        </select>
                    </div>
                </div>

                {/* JSON tools */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '10px 0', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', marginBottom: 14 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', alignSelf: 'center', marginRight: 4 }}>JSON:</span>
                    <button onClick={() => importRef.current?.click()} style={jBtn('#0ea5e9')}>⬆ Import</button>
                    <button onClick={exportJSON}                        style={jBtn('#0ea5e9')}>⬇ Export</button>
                    <button onClick={() => { navigator.clipboard.writeText(JSON.stringify(doc, null, 2)); showToast('Copied!') }} style={jBtn('#64748b')}>📋 Copy</button>
                    <button onClick={() => setPreview(true)}           style={jBtn('#7c3aed')}>👁 Preview</button>
                </div>

                {/* editor */}
                <div style={{ maxHeight: '55vh', overflowY: 'auto', paddingRight: 4 }}>
                    {cats.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '30px 0', border: '2px dashed #e2e8f0', borderRadius: 10, marginBottom: 8 }}>No categories yet — click <strong>+ Add Category</strong></div>}
                    {cats.map((cat, ci) => <CategoryCard key={ci} cat={cat} ci={ci} lang={language} doc={doc} setDoc={setDoc} />)}
                    <button onClick={() => { const d = cp(doc); d.referenceListDetails.functions.categories.push(mk.category()); setDoc(d) }}
                        style={{ display: 'block', width: '100%', padding: '12px 0', background: '#ede9fe', color: '#7c3aed', border: '2px dashed #c4b5fd', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 8 }}>
                        + Add Category
                    </button>
                </div>

                {/* save bar */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 16, marginTop: 4, borderTop: '1px solid #f1f5f9' }}>
                    <button onClick={onBack} style={{ padding: '10px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                    <button onClick={save} disabled={saving} style={{ padding: '10px 28px', background: saving ? '#94a3b8' : '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: saving ? 'none' : '0 2px 8px rgba(124,58,237,.3)' }}>
                        {saving ? 'Saving…' : 'Save & Publish'}
                    </button>
                </div>
            </div>
        </div>
    )
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function ScriptingExampleCodeAdmin() {
    const [toast, showToast] = useToast()
    // view: 'list' | 'edit'
    const [view, setView]   = useState('list')
    const [editData, setEditData] = useState(null)

    const openEdit = (data) => { setEditData(data); setView('edit') }
    const backToList = () => { setEditData(null); setView('list') }

    return (
        <>
            <Toast t={toast} />
            {view === 'list'
                ? <ListView onEdit={openEdit} showToast={showToast} />
                : <EditView initial={editData} onBack={backToList} showToast={showToast} />}
        </>
    )
}

function actionBtn(color) {
    return { padding: '5px 10px', borderRadius: 6, border: `1px solid ${color}33`, background: `${color}11`, color, fontWeight: 600, fontSize: 12, cursor: 'pointer' }
}
function jBtn(color) {
    return { padding: '5px 12px', borderRadius: 6, border: `1px solid ${color}33`, background: `${color}11`, color, fontWeight: 600, fontSize: 12, cursor: 'pointer' }
}
